/**
 * Shared formatting utilities for dates, times, and display values.
 * Used by teacher and admin experiences so tables and reports stay consistent.
 */

import i18nInstance from '@/locales/i18n';

const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const getMonthName = (monthIndex: number): string =>
  i18nInstance.t(`months.${MONTH_KEYS[monthIndex]}`);

/**
 * Format a Date object to standard format (d MMM yyyy)
 * @param date - Date object
 * @returns Formatted date string (e.g., "25 Oct 2025")
 */
export const formatDateToStandard = (date: Date): string => {
  const day = date.getDate();
  const month = getMonthName(date.getMonth());
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

/**
 * Format session time from seconds to human readable format
 * @param seconds - Time in seconds
 * @returns Formatted time string (e.g., "3h 45m", "45m", "30s")
 */
export const formatSessionTime = (seconds: number): string => {
  if (seconds === 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (remainingSeconds > 0 && hours === 0) {
    parts.push(`${remainingSeconds}s`);
  }

  return parts.join(' ') || '0s';
};

/**
 * Format date string to human readable format
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "25 Oct 2025")
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return formatDateToStandard(date);
  } catch (error) {
    return dateString;
  }
};

/**
 * Format date with time to human readable format
 * @param dateString - ISO date string
 * @returns Formatted date and time string (e.g., "25 Oct 2025 at 8:30 AM")
 */
export const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const formattedDate = formatDateToStandard(date);
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

    return `${formattedDate} at ${formattedTime}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Format date with 24-hour time and a pipe separator (e.g. history tables)
 * @param dateString - ISO date string
 * @returns Formatted date and time string (e.g., "25 Oct 2025 | 08:30")
 */
export const formatDateTimePipe = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const formattedDate = formatDateToStandard(date);
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${formattedDate} | ${formattedTime}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Format date with 24-hour time format
 * @param dateString - ISO date string
 * @returns Formatted date and time string (e.g., "21 Dec 2025 13:05")
 */
export const formatDateWith24HourTime = (dateString: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const formattedDate = formatDateToStandard(date);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${formattedDate} ${hours}:${minutes}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Format numbers with appropriate suffixes (K, M, B)
 * @param num - Number to format
 * @returns Formatted number string (e.g., "1.2K", "1.5M")
 */
export const formatLargeNumber = (num: number): string => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

/**
 * Format percentage with proper rounding
 * @param value - Decimal value (e.g., 0.1234)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "12.3%")
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return (value * 100).toFixed(decimals) + '%';
};

/**
 * Format file size in bytes to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted size string (e.g., "1.2 MB", "500 KB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, unitIndex);

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Format date string to short format with abbreviated month
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "25 Oct 2025")
 */
export const formatDateShort = (dateString: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return formatDateToStandard(date);
  } catch (error) {
    return dateString;
  }
};

/**
 * Format time from seconds to human readable format.
 * Values are rounded to the nearest whole second before splitting into units.
 * - If < 1 hour: shows minutes and seconds (e.g., "45m 30s")
 * - If >= 1 hour: shows hours and minutes (e.g., "2h 15m")
 */
export const formatMinutes = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0m 0s';
  }

  const totalSeconds = Math.max(0, Math.round(seconds));

  // If less than 1 hour, show minutes and seconds
  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    return `${minutes}m ${secs}s`;
  }

  // If 1 hour or more, show hours and minutes
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${hours}h ${minutes}m`;
};

/**
 * Format duration for PDF reports (e.g. "2h 15m 30s", "45m 30s")
 */
export const formatReportTime = (time: number): string => {
  let seconds = time && time > 0 ? time : 0;

  const days = Math.floor(seconds / (60 * 60 * 24));
  seconds = seconds % (60 * 60 * 24);

  const hours = Math.floor(seconds / (60 * 60));
  const hoursStr = `0${hours}`.slice(-2);
  seconds = seconds % (60 * 60);

  const minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;

  const secondsStr = `0${seconds}`.slice(-2);

  return days
    ? `${days}d ${hours}h ${minutes}m ${secondsStr}s`
    : hours
      ? `${hoursStr}h ${minutes}m ${secondsStr}s`
      : minutes
        ? `${minutes}m ${secondsStr}s`
        : `${secondsStr}s`;
};

/**
 * Format date for PDF reports; matches table date style (d MMM yyyy).
 * RTL languages use year-month-day order.
 */
export const formatReportDate = (_date: Date | string | number, language: string): string => {
  const date = new Date(_date);
  const year = date.getFullYear();
  const yearStr = `${year}`;
  const month = getMonthName(date.getMonth());
  const day = date.getDate();
  if (language === 'he' || language === 'ar') {
    return `${yearStr} ${month} ${day}`;
  }
  return `${day} ${month} ${yearStr}`;
};
